import React, { useEffect, useRef } from 'react';
import { X, Braces } from 'lucide-react';
import RichTextInput from './RichTextInput/RichTextInput';
import SlotTokenRenderer from '../../BotBuilder/ResponseNode/ResponseBlock/SlotTokenRenderer';

interface SampleUtteranceItemProps {
  readonly utterance: string;
  readonly index: number;
  readonly isEditing: boolean;
  readonly editedValue: string;
  readonly onEditStart: (index: number, utterance: string) => void;
  readonly onEditChange: (value: string) => void;
  readonly onEditFinish: (index: number) => void;
  readonly onDelete: (index: number) => void;
  readonly onSlotClick: (chip: HTMLSpanElement) => void;
  readonly onBracesMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
  readonly isLocked?: boolean;
}

const ITEM_CLASS =
  'flex w-full items-center justify-between rounded-md bg-white ' +
  'px-3 py-2 text-sm text-black';

function SampleUtteranceItem({
  utterance,
  index,
  isEditing,
  editedValue,
  onEditStart,
  onEditChange,
  onEditFinish,
  onDelete,
  onSlotClick,
  onBracesMouseDown,
  isLocked = false
}: SampleUtteranceItemProps): React.ReactElement {
  const editingInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing && editingInputRef.current) {
      editingInputRef.current.focus();
      const sel = globalThis.getSelection?.();
      const range = document.createRange();
      range.selectNodeContents(editingInputRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [isEditing]);

  const slotAdornment = (
    <button
      type='button'
      tabIndex={-1}
      onMouseDown={onBracesMouseDown}
      aria-label='Insert variable'
      className='ml-2 flex shrink-0 items-center justify-center rounded p-1 text-black/50 hover:text-black'
    >
      <Braces size={16} />
    </button>
  );

  if (isEditing) {
    return (
      <div className={`${ITEM_CLASS} px-3 py-2`}>
        <RichTextInput
          ref={editingInputRef}
          value={editedValue}
          onChange={onEditChange}
          placeholder='Edit trigger…'
          fullWidth
          transparent
          compact
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              onEditFinish(index);
            }
          }}
          onSlotClick={onSlotClick}
          InputProps={{ endAdornment: slotAdornment }}
          inputProps={
            {
              'data-editing-utterance': true
            } as React.HTMLAttributes<HTMLDivElement>
          }
        />
      </div>
    );
  }

  return (
    <div
      className={`${ITEM_CLASS} group text-left transition-colors ${
        isLocked ? 'cursor-default' : 'cursor-text hover:bg-white/90'
      }`}
    >
      <button
        type='button'
        disabled={isLocked}
        className='min-w-0 flex-1 text-left'
        onClick={() => {
          onEditStart(index, utterance);
        }}
      >
        <span className='block whitespace-pre-wrap break-words leading-snug'>
          <SlotTokenRenderer>{utterance}</SlotTokenRenderer>
        </span>
      </button>
      {!isLocked && (
        <button
          type='button'
          aria-label='Delete utterance'
          className='ml-2 shrink-0 rounded p-1 text-black/50 transition-colors hover:text-black'
          onClick={() => {
            onDelete(index);
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

export default SampleUtteranceItem;
